/**
 * <TextButton> — le bouton tertiaire (Buttons family).
 *
 * The maison replacement for legacy `TextButtonM`: a pressable label, no pill chrome. Sibling of
 * `<Button>` / `<LabeledIconButton>` (different visual language — not a `variant` of the pill).
 *
 * Brand core V13, chap. 6 : « Tertiaire : texte seul, souligné au survol, jamais au repos. »
 * Le survol n'existe que sur le web ; au doigt, le lien reste nu et c'est le retour de pression
 * qui répond.
 *
 * No hex literals: color comes from a `SemanticColorKey`.
 */
import React from "react"
import { ActivityIndicator, Pressable, type GestureResponderEvent } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../../theme/tokens/colors"
import { type FontSizeKey } from "../../../theme/tokens/typography"
import { FocusRing, focusRingHost } from "../../FocusRing"
import { Text } from "../../typography/Text"

type TextButtonSize = "sm" | "md" | "lg"

/**
 * `<Pressable>` ne type que `pressed` ; react-native-web ajoute `hovered` et `focused` à
 * l'exécution. Les déclarer optionnels laisse le callback accepter l'état natif tel quel.
 */
type PressableVisualState = { pressed: boolean; hovered?: boolean; focused?: boolean }

export interface TextButtonProps {
    label: string
    /** Label color token — défaut `text-link`, l'univers écrit à même la surface. Pressed state fades. */
    color?: SemanticColorKey
    size?: TextButtonSize
    /** Stretch to the container width (label stays centered). */
    fullWidth?: boolean
    onPress?: (event: GestureResponderEvent) => void
    disabled?: boolean
    loading?: boolean
    testID?: string
    accessibilityLabel?: string
}

const SIZE_TO_TEXT: Record<TextButtonSize, FontSizeKey> = {
    sm: "text-md",
    md: "text-base",
    lg: "text-lg",
}

export function TextButton(props: TextButtonProps) {
    const {
        label,
        color = "text-link",
        size = "md",
        fullWidth = false,
        onPress,
        disabled = false,
        loading = false,
        testID,
        accessibilityLabel,
    } = props

    const { theme } = useUnistyles()
    const isBlocked = disabled || loading

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={isBlocked}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityState={{ disabled: isBlocked }}
            style={({ pressed }: PressableVisualState) => [
                styles.root(fullWidth, isBlocked),
                focusRingHost,
                pressed && !isBlocked ? styles.pressed : undefined,
            ]}
        >
            {({ hovered, focused }: PressableVisualState) =>
                loading ? (
                    <ActivityIndicator size="small" color={theme.colors[color]} />
                ) : (
                    <>
                        <FocusRing visible={focused === true && !isBlocked} radius={theme.radius.sm} />
                        <Text
                            size={SIZE_TO_TEXT[size]}
                            color={color}
                            center
                            weight="bold"
                            style={hovered === true ? styles.underline : undefined}
                        >
                            {label}
                        </Text>
                    </>
                )
            }
        </Pressable>
    )
}

const styles = StyleSheet.create(() => ({
    root: (fullWidth: boolean, disabled: boolean) => ({
        width: fullWidth ? ("100%" as const) : undefined,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: {
        opacity: 0.7,
    },
    underline: {
        textDecorationLine: "underline" as const,
    },
}))
